/**
 * Type-safe environment variable access
 *
 * All environment variables must be prefixed with VITE_ to be exposed to the client
 */

interface EnvironmentConfig {
  // API Configuration
  apiUrl: string;
  apiTimeout: number;

  // Application Configuration
  appName: string;
  appVersion: string;
  appDescription: string;

  // Environment
  nodeEnv: string;
  isDevelopment: boolean;
  isProduction: boolean;
  isTest: boolean;

  // Authentication
  authTokenKey: string;
  authRefreshTokenKey: string;
  authTokenExpiry: number;

  // Feature Flags
  enableAnalytics: boolean;
  enableErrorTracking: boolean;
  enableDebugMode: boolean;
  enablePayloadEncryption: boolean;
  logLevel: string;

  // Payload Encryption
  payloadEncryptionKey: string;

  // Storage
  storagePrefix: string;

  // WebSocket
  wsUrl: string;

  // Pagination
  defaultPageSize: number;
  maxPageSize: number;

  // File Upload
  maxFileSize: number;
  allowedFileTypes: string[];

  // Rate Limiting
  rateLimitRequests: number;
  rateLimitWindow: number;
}

/**
 * Get environment variable with type safety
 */
const getEnv = (key: string, defaultValue?: string): string => {
  const value = import.meta.env[key] || defaultValue;
  if (value === undefined) {
    console.warn(`Environment variable ${key} is not defined`);
    return '';
  }
  return value;
};

/**
 * Get boolean environment variable
 */
const getBoolEnv = (key: string, defaultValue = false): boolean => {
  const value = getEnv(key, defaultValue.toString());
  return value === 'true' || value === '1';
};

/**
 * Get number environment variable
 */
const getNumberEnv = (key: string, defaultValue: number): number => {
  const value = getEnv(key, defaultValue.toString());
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Get array environment variable (comma-separated)
 */
const getArrayEnv = (key: string, defaultValue: string[] = []): string[] => {
  const value = getEnv(key);
  if (!value) return defaultValue;
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

// Determine current environment
const nodeEnv = getEnv('VITE_NODE_ENV', import.meta.env.MODE || 'development');

/**
 * Centralized environment configuration
 */
export const env: EnvironmentConfig = {
  // API Configuration
  apiUrl: getEnv('VITE_API_URL', 'http://localhost:8787'),
  apiTimeout: getNumberEnv('VITE_API_TIMEOUT', 30000),

  // Application Configuration
  appName: getEnv('VITE_APP_NAME', 'Orbit'),
  appVersion: getEnv('VITE_APP_VERSION', '1.0.0'),
  appDescription: getEnv('VITE_APP_DESCRIPTION', 'Creator Operating System'),

  // Environment
  nodeEnv,
  isDevelopment: nodeEnv === 'development',
  isProduction: nodeEnv === 'production',
  isTest: nodeEnv === 'test',

  // Authentication
  authTokenKey: getEnv('VITE_AUTH_TOKEN_KEY', 'creatoros_auth_token'),
  authRefreshTokenKey: getEnv('VITE_AUTH_REFRESH_TOKEN_KEY', 'creatoros_refresh_token'),
  authTokenExpiry: getNumberEnv('VITE_AUTH_TOKEN_EXPIRY', 3600000), // 1 hour

  // Feature Flags
  enableAnalytics: getBoolEnv('VITE_ENABLE_ANALYTICS', false),
  enableErrorTracking: getBoolEnv('VITE_ENABLE_ERROR_TRACKING', nodeEnv !== 'production'),
  enableDebugMode: getBoolEnv('VITE_ENABLE_DEBUG_MODE', nodeEnv === 'development'),
  enablePayloadEncryption: getBoolEnv('VITE_ENABLE_PAYLOAD_ENCRYPTION', false),
  logLevel: getEnv('VITE_LOG_LEVEL', nodeEnv === 'development' ? 'debug' : 'info'),

  // Payload Encryption
  payloadEncryptionKey: getEnv('VITE_PAYLOAD_ENCRYPTION_KEY', ''),

  // Storage
  storagePrefix: getEnv('VITE_STORAGE_PREFIX', 'creatoros_'),

  // WebSocket
  wsUrl: getEnv('VITE_WS_URL', 'ws://localhost:4000'),

  // Pagination
  defaultPageSize: getNumberEnv('VITE_DEFAULT_PAGE_SIZE', 20),
  maxPageSize: getNumberEnv('VITE_MAX_PAGE_SIZE', 100),

  // File Upload
  maxFileSize: getNumberEnv('VITE_MAX_FILE_SIZE', 10485760), // 10MB
  allowedFileTypes: getArrayEnv('VITE_ALLOWED_FILE_TYPES', [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
  ]),

  // Rate Limiting
  rateLimitRequests: getNumberEnv('VITE_RATE_LIMIT_REQUESTS', 100),
  rateLimitWindow: getNumberEnv('VITE_RATE_LIMIT_WINDOW', 60000), // 1 minute
};

/**
 * Validate required environment variables
 */
export const validateEnv = (): void => {
  const requiredVars = ['VITE_API_URL', 'VITE_APP_NAME'];

  const missing = requiredVars.filter((varName) => !import.meta.env[varName]);

  if (missing.length > 0) {
    console.error(`Missing required environment variables: ${missing.join(', ')}`);
  }
};

// Validate on module load
if (env.isDevelopment) {
  validateEnv();
}

export default env;
